/**
 * BIGWA - Outline Types v2.2
 * Fase 6: Outliner + Rascunho + Enviar ao Outline
 * 
 * v2.0: Adicionado OutlineItem e SelectedBlockContent para feature "Enviar ao Outline"
 *       Estrutura compatível com outlineStore.ts v3.0
 * v2.1: Adicionado tipo 'synthesis' para suportar sínteses no Outline
 * v2.2: Removido campo tab do OutlineItemSource (simplificação - só teoria-teoria)
 */

// ============================================================
// SEÇÃO DO OUTLINE
// ============================================================

export interface OutlineSection {
  id: string;
  title: string;
  parentId: string | null;
  order: number;
  level: number;
  isCollapsed: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================================
// ALOCAÇÃO DE BLOCOS (LEGADO)
// ============================================================

export interface OutlineItemPlacement {
  id: string;
  blockId: string;
  sectionId: string;
  order: number;
  createdAt: string;
}

// ============================================================
// NOTA LIVRE
// ============================================================

export interface OutlineNote {
  id: string;
  sectionId: string;
  text: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================
// 🆕 v2.0: TIPO DE CONTEÚDO SELECIONÁVEL
// ============================================================

export type SelectableContentType = 'quote' | 'translation' | 'paraphrase' | 'comment' | 'synthesis';

// ============================================================
// 🆕 v2.0: REFERÊNCIA DO ITEM
// ============================================================

export interface OutlineItemReference {
  id: string;
  title: string;
  authors: string;
  year?: string;
  page?: string;
}

// ============================================================
// 🆕 v2.0: FONTE DO ITEM
// v2.2: Removido campo tab (simplificação)
// ============================================================

export interface OutlineItemSource {
  blockId: string;
  highlightId: string;
  tag?: string;
}

// ============================================================
// 🆕 v2.0: ITEM DO OUTLINE (Enviado de Conexões)
// ============================================================

export interface OutlineItem {
  /** ID único do item */
  id: string;
  
  /** Tipo de conteúdo */
  type: SelectableContentType;
  
  /** Texto do conteúdo */
  content: string;
  
  /** Referência bibliográfica */
  reference: OutlineItemReference;
  
  /** Fonte (de onde veio) */
  source: OutlineItemSource;
  
  /** ID da seção (null = inbox) */
  sectionId: string | null;
  
  /** Ordem dentro da seção/inbox */
  order: number;
  
  /** Nível de indentação (0-3) */
  level: number;
  
  /** Data de criação */
  createdAt: string;
  
  /** Última atualização */
  updatedAt?: string;
}

// ============================================================
// 🆕 v2.0: CONTEÚDO SELECIONADO EM CONEXÕES
// ============================================================

export interface SelectedBlockContent {
  /** ID do bloco (highlightId) */
  blockId: string;
  
  /** Tipo de conteúdo */
  type: SelectableContentType;
  
  /** Texto do conteúdo */
  content: string;
  
  /** Referência bibliográfica */
  reference: OutlineItemReference;
  
  /** Tag do bloco */
  tag?: string;
}

// ============================================================
// PROJETO OUTLINE (PERSISTÊNCIA)
// ============================================================

export interface ProjectOutline {
  sections: OutlineSection[];
  placements: OutlineItemPlacement[];
  items?: OutlineItem[];
  notes?: OutlineNote[];
  lastModified: string;
}

// ============================================================
// ESTADO DA JANELA FLUTUANTE
// ============================================================

export interface OutlinePanelState {
  isOpen: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  focusedSectionId: string | null;
  focusedBlockId: string | null;
}

// ============================================================
// PREFERÊNCIAS DE VISUALIZAÇÃO
// ============================================================

export interface OutlineViewPreferences {
  density: 'compact' | 'normal' | 'comfortable';
  showParaphrases: boolean;
  showTreeLines: boolean;
}

// ============================================================
// EXPORTAÇÃO PARA RASCUNHO
// ============================================================

export interface DraftExportOptions {
  documentTitle: string;
  includeCitations: boolean;
  includeParaphrases: boolean;
  includeNotes: boolean;
  includeReferences: boolean;
  citationStyle: 'abnt' | 'apa' | 'chicago' | 'vancouver';
  exportFormat: 'google-docs' | 'markdown' | 'docx';
}

// ============================================================
// TIPOS AUXILIARES
// ============================================================

export type MoveDirection = 'up' | 'down';

export interface SectionWithBlocks extends OutlineSection {
  blocks: OutlineItemPlacement[];
  items?: OutlineItem[];
  notes: OutlineNote[];
  children: SectionWithBlocks[];
}

export interface OutlineBlockData {
  id: string;
  tag: string;
  author: string;
  year?: string;
  excerpt: string;
  paraphrase?: string;
}

// ============================================================
// CONSTANTES
// ============================================================

export const DEFAULT_PANEL_STATE: OutlinePanelState = {
  isOpen: false,
  position: { x: 100, y: 100 },
  size: { width: 540, height: 640 },
  focusedSectionId: null,
  focusedBlockId: null,
};

export const DEFAULT_VIEW_PREFERENCES: OutlineViewPreferences = {
  density: 'normal',
  showParaphrases: true,
  showTreeLines: true,
};

export const DEFAULT_EXPORT_OPTIONS: DraftExportOptions = {
  documentTitle: 'Rascunho',
  includeCitations: true,
  includeParaphrases: true,
  includeNotes: false,
  includeReferences: false,
  citationStyle: 'abnt',
  exportFormat: 'google-docs',
};

export const EMPTY_PROJECT_OUTLINE: ProjectOutline = {
  sections: [],
  placements: [],
  items: [],
  notes: [],
  lastModified: new Date().toISOString(),
};

// 🆕 v2.0: Labels para tipos de conteúdo
export const CONTENT_TYPE_LABELS: Record<SelectableContentType, string> = {
  quote: 'Citação',
  translation: 'Tradução',
  paraphrase: 'Paráfrase',
  comment: 'Comentário',
  synthesis: 'Síntese',
};

// 🆕 v2.0: Cores para tipos de conteúdo
export const CONTENT_TYPE_COLORS: Record<SelectableContentType, string> = {
  quote: '#477B72',
  translation: '#55B3C5',
  paraphrase: '#8B6914',
  comment: '#D4918A',
  synthesis: '#7B68EE',  // Roxo - igual página Citações
};
